import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";

type TooltipArrowDirection = "up" | "right" | "down" | "left";
type TooltipTextAlign = "left" | "center" | "right";

interface TooltipProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "content"
> {
  text?: string;
  content?: React.ReactNode;
  iconName?: string;
  arrow?: TooltipArrowDirection;
  textAlign?: TooltipTextAlign;
  backgroundColor?: string;
  textColor?: string;
  minWidth?: number;
  maxWidth?: number;
}

interface TooltipHoverProps extends TooltipProps {
  children: React.ReactNode;
  offset?: number;
  fullWidth?: boolean;
}

const TOOLTIP_PADDING_VERTICAL = 16;
const TOOLTIP_PADDING_HORIZONTAL = 12;
const TOOLTIP_BORDER_RADIUS = 16;
const ARROW_SIZE = 18;
const DEFAULT_OFFSET = 12;

const getTooltipContainerProps = (
  props: TooltipProps,
): React.HTMLAttributes<HTMLDivElement> => {
  const {
    text: _text,
    content: _content,
    iconName: _iconName,
    arrow: _arrow,
    textAlign: _textAlign,
    backgroundColor: _backgroundColor,
    textColor: _textColor,
    minWidth: _minWidth,
    maxWidth: _maxWidth,
    ...rest
  } = props;

  return rest;
};

const getTooltipPropsFromHover = (props: TooltipHoverProps): TooltipProps => {
  const { children: _children, offset: _offset, ...rest } = props;
  return rest;
};

/**
 * Tooltip bubble that renders optional icon, text, and an arrow.
 */
export const Tooltip = (props: TooltipProps): React.ReactElement => {
  const {
    text,
    content,
    iconName,
    arrow: arrowProp,
    textAlign: textAlignProp,
    backgroundColor: backgroundColorProp,
    textColor: textColorProp,
    minWidth: minWidthProp,
    maxWidth: maxWidthProp,
  } = props;
  const hasIcon = Boolean(iconName);
  const resolvedContent = content ?? text ?? "";
  const arrow = arrowProp ?? "down";
  const textAlign = textAlignProp ?? (hasIcon ? "left" : "center");
  const backgroundColor = backgroundColorProp ?? ColorV2.Blue6;
  const textColor = textColorProp ?? ColorV2.Blue3;
  const minWidth = minWidthProp ?? 140;
  const maxWidth = maxWidthProp ?? 220;
  const containerProps = getTooltipContainerProps(props);

  return (
    <Container
      $hasIcon={hasIcon}
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      {...containerProps}
    >
      {iconName && (
        <IconWrapper>
          <CustomIcon name={iconName} size="24px" color={ColorV2.Blue3} />
        </IconWrapper>
      )}
      <Content
        $hasIcon={hasIcon}
        $textAlign={textAlign}
        $minWidth={minWidth}
        $maxWidth={maxWidth}
      >
        {typeof resolvedContent === "string" ? (
          <Text $hasIcon={hasIcon}>{resolvedContent}</Text>
        ) : (
          resolvedContent
        )}
      </Content>
      <Arrow $direction={arrow} $backgroundColor={backgroundColor} />
    </Container>
  );
};

/**
 * Wraps a trigger and shows the tooltip bubble on hover.
 */
export const TooltipHover = (props: TooltipHoverProps): React.ReactElement => {
  const {
    children,
    arrow: arrowProp,
    offset: offsetProp,
    backgroundColor,
    textColor,
    fullWidth,
  } = props;
  const arrow = arrowProp ?? "down";
  const offset = offsetProp ?? DEFAULT_OFFSET;
  const tooltipProps = getTooltipPropsFromHover(props);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [isPressedOpen, setIsPressedOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isOpen = isHoverOpen || isPressedOpen;

  useEffect(() => {
    if (!isPressedOpen) return;
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setIsPressedOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isPressedOpen]);

  return (
    <HoverWrap
      ref={wrapperRef}
      onMouseEnter={() => setIsHoverOpen(true)}
      onMouseLeave={() => setIsHoverOpen(false)}
      $fullWidth={Boolean(fullWidth)}
    >
      <Trigger
        onClick={() => {
          setIsPressedOpen((prev) => !prev);
          setIsHoverOpen(true);
        }}
        $fullWidth={Boolean(fullWidth)}
      >
        {children}
        <Bubble $placement={arrow} $offset={offset} $isOpen={isOpen}>
          <Tooltip
            {...tooltipProps}
            arrow={arrow}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        </Bubble>
      </Trigger>
    </HoverWrap>
  );
};

const Container = styled.div<{
  $hasIcon: boolean;
  $backgroundColor: string;
  $textColor: string;
}>`
  ${TypographyV2.s1}
  position: relative;
  width: fit-content;
  padding: ${TOOLTIP_PADDING_VERTICAL}px ${TOOLTIP_PADDING_HORIZONTAL}px;
  border-radius: ${TOOLTIP_BORDER_RADIUS}px;
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  display: flex;
  gap: 16px;
  align-items: center;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const Content = styled.div<{
  $hasIcon: boolean;
  $textAlign: TooltipTextAlign;
  $minWidth: number;
  $maxWidth: number;
}>`
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 auto;
  min-width: ${(props) => props.$minWidth}px;
  max-width: ${(props) => props.$maxWidth}px;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
  text-align: ${(props) => props.$textAlign};
  align-items: ${(props) => {
    if (props.$textAlign === "right") return "flex-end";
    if (props.$textAlign === "center") return "center";
    return "flex-start";
  }};

  ${(props) =>
    !props.$hasIcon &&
    css`
      align-items: ${props.$textAlign === "right"
        ? "flex-end"
        : props.$textAlign === "center"
          ? "center"
          : "flex-start"};
      text-align: ${props.$textAlign};
    `}
`;

const Text = styled.span<{ $hasIcon: boolean }>`
  ${TypographyV2.s1}
  color: inherit;
  ${(props) =>
    props.$hasIcon &&
    css`
      text-align: left;
    `}
`;

const Arrow = styled.span<{
  $direction: TooltipArrowDirection;
  $backgroundColor: string;
}>`
  position: absolute;
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  background-color: ${(props) => props.$backgroundColor};
  transform: rotate(45deg);

  ${(props) =>
    props.$direction === "up" &&
    css`
      top: -${ARROW_SIZE / 2}px;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "down" &&
    css`
      bottom: -${ARROW_SIZE / 2}px;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "left" &&
    css`
      left: -${ARROW_SIZE / 2}px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "right" &&
    css`
      right: -${ARROW_SIZE / 2}px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
    `}
`;

const Bubble = styled.div<{
  $placement: TooltipArrowDirection;
  $offset: number;
  $isOpen: boolean;
}>`
  position: absolute;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  transform: translate(
    ${(props) => (props.$isOpen ? "var(--tx-hover)" : "var(--tx)")},
    ${(props) => (props.$isOpen ? "var(--ty-hover)" : "var(--ty)")}
  );

  ${(props) =>
    props.$placement === "down" &&
    css`
      bottom: calc(100% + ${props.$offset}px);
      left: 50%;
      --tx: -50%;
      --ty: 4px;
      --tx-hover: -50%;
      --ty-hover: 0;
    `}

  ${(props) =>
    props.$placement === "up" &&
    css`
      top: calc(100% + ${props.$offset}px);
      left: 50%;
      --tx: -50%;
      --ty: -4px;
      --tx-hover: -50%;
      --ty-hover: 0;
    `}

  ${(props) =>
    props.$placement === "left" &&
    css`
      left: calc(100% + ${props.$offset}px);
      top: 50%;
      --tx: 4px;
      --ty: -50%;
      --tx-hover: 0;
      --ty-hover: -50%;
    `}

  ${(props) =>
    props.$placement === "right" &&
    css`
      right: calc(100% + ${props.$offset}px);
      top: 50%;
      --tx: -4px;
      --ty: -50%;
      --tx-hover: 0;
      --ty-hover: -50%;
    `}
`;

const HoverWrap = styled.div<{ $fullWidth: boolean }>`
  display: ${(props) => (props.$fullWidth ? "flex" : "inline-flex")};
  align-items: center;
  align-self: flex-start;
  width: ${(props) => (props.$fullWidth ? "100%" : "fit-content")};
  max-width: 100%;
`;

const Trigger = styled.span<{ $fullWidth: boolean }>`
  position: relative;
  display: ${(props) => (props.$fullWidth ? "flex" : "inline-flex")};
  align-items: center;
  width: ${(props) => (props.$fullWidth ? "100%" : "auto")};
`;
